import React, { useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import ProformaInvoiceModel, { 
  ProformaInvoiceData, 
  getDefaultProformaInvoiceData, 
  proformaInvoiceSchema 
} from "../../core/api/invoice/ProformaInvoiceModel";
import InvoiceModalView from "./InvoiceModalView";
import ClientModel from "../../core/api/clients/ClientModel";
import { DatePicker } from "../common/DatePicker";
import { StyledTextField } from "../common/StyledComponents";
import { translations } from "../../translations/common";
import { useNotifications } from "hooks/NotificationProvider";

interface Props {
  clientId: string;
  close(): void;
  onSuccess(): void;
}

interface PreviewableInvoice extends ProformaInvoiceData {
  preview?: boolean;
}

interface PreviewState {
  preview: boolean;
  pdfUrl?(): Promise<string>;
}

const ProformaInvoiceModalForm: React.FC<Props> = (props) => {
  const closePopup = props.close;
  const notifications = useNotifications();
  const [preview, setPreview] = useState<PreviewState>({ preview: false });
  const [form, setForm] = useState<PreviewableInvoice>(() => ({
    ...getDefaultProformaInvoiceData(),
  }));

  useEffect(() => {
    ClientModel.findOne(props.clientId).then((client) => {
      if (client.validFrom && client.validTo) {
        const updatedForm = ProformaInvoiceModel.createWithDates(client.validFrom, client.validTo);
        setForm(prevForm => ({
          ...prevForm,
          ...updatedForm
        }));
      }
    });
  }, [props.clientId]);

  return (
    <div>
      <Dialog
        open={true}
        onClose={closePopup}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
      >
        <DialogTitle id="form-dialog-title">
          {translations.invoice.form.title}
        </DialogTitle>
        <Formik
          initialValues={form}
          enableReinitialize={true}
          validationSchema={proformaInvoiceSchema}
          onSubmit={(values, actions) => {
            if (values.preview) {
              setForm(values);
              setPreview({
                preview: true,
                pdfUrl: () =>
                  ProformaInvoiceModel.getPdfPreview(values, props.clientId),
              });
              actions.setSubmitting(false);
            } else {
              return ProformaInvoiceModel.save(values, props.clientId)
                .then((id) => {
                  closePopup();
                  props.onSuccess();
                })
                .catch((error) => {
                   notifications.notify({
                    level: "error",
                    message: error.response?.data?.description || "Failed to create invoice",
                  });
                  if (error.response?.data?.errors) {
                    error.response.data.errors.forEach((element: any) => {
                      actions.setFieldError(
                        element.field,
                        element.defaultMessage
                      );
                    });
                  }
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            }
          }}
        >
          {(formikProps: FormikProps<PreviewableInvoice>) => {
            const {
              values,
              errors,
              handleBlur,
              handleChange,
              isSubmitting,
              setFieldValue,
            } = formikProps;
            return (
              <Form>
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <DatePicker
                        id="date"
                        name="date"
                        label={translations.invoice.form.fields.date}
                        value={values.date}
                        onChange={handleChange}
                        error={!!errors.date}
                        helperText={errors.date}
                        fullWidth
                      />
                    </Grid>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <DatePicker
                        id="periodFrom"
                        name="periodFrom"
                        label={translations.invoice.form.fields.periodFrom}
                        value={values.periodFrom}
                        onChange={(e) => {
                          const newPeriodFrom = e.target.value;
                          handleChange(e);
                          // Update productPeriod when periodFrom changes
                          if (newPeriodFrom && values.periodTo) {
                            setFieldValue("productPeriod", `${newPeriodFrom} - ${values.periodTo}`);
                          }
                        }}
                        error={!!errors.periodFrom}
                        helperText={errors.periodFrom}
                        fullWidth
                      />
                    </Grid>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <DatePicker
                        id="periodTo"
                        name="periodTo"
                        label={translations.invoice.form.fields.periodTo}
                        value={values.periodTo}
                        onChange={(e) => {
                          const newPeriodTo = e.target.value;
                          handleChange(e);
                          // Update productPeriod when periodTo changes
                          if (values.periodFrom && newPeriodTo) {
                            setFieldValue("productPeriod", `${values.periodFrom} - ${newPeriodTo}`);
                          }
                        }}
                        error={!!errors.periodTo}
                        helperText={errors.periodTo}
                        fullWidth
                      />
                    </Grid>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <NumericFormat
                        customInput={StyledTextField}
                        name="price"
                        id="price"
                        label={translations.invoice.form.fields.price}
                        value={values.price ?? ""}
                        fullWidth
                        allowedDecimalSeparators={[".", ","]}
                        helperText={errors.price}
                        error={Boolean(errors.price)}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <StyledTextField
                        name="jobTitle"
                        id="jobTitle"
                        label={translations.invoice.form.fields.jobTitle}
                        value={values.jobTitle}
                        type="text"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <StyledTextField
                        name="product"
                        id="product"
                        label={translations.invoice.form.fields.product}
                        value={values.product}
                        type="text"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid component="div" sx={{ width: '100%' }}>
                      <StyledTextField
                        name="productPeriod"
                        id="productPeriod"
                        label={translations.invoice.form.fields.productPeriod}
                        value={values.productPeriod}
                        type="text"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => {
                      setFieldValue("preview", true);
                    }}
                  >
                    {translations.invoice.form.buttons.preview}
                  </Button>
                  <Button
                    type="button"
                    className="outline"
                    onClick={closePopup}
                    disabled={isSubmitting}
                  >
                    {translations.invoice.form.buttons.cancel}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => setFieldValue("preview", false)}
                    variant="contained"
                    color="primary"
                  >
                    {translations.invoice.form.buttons.save}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
      {preview && preview.pdfUrl && (
        <InvoiceModalView
          close={() => setPreview({ preview: false })}
          pdfUrl={preview.pdfUrl}
        />
      )}
    </div>
  );
};

export default ProformaInvoiceModalForm;
